/**
 * Baraka Pay's sandbox test-instrument table.
 *
 * A reference panel for whoever is testing a payment, proxied by our own API
 * (`GET /payments/sandbox-instruments`) because Baraka Pay serves no cross-origin
 * traffic. Never copied into this repository: a copy is a second source of truth
 * for how a rail behaves, and the copy is the one a tester would trust.
 *
 * THERE IS NO ENVIRONMENT FLAG, deliberately. Baraka Pay serves this in Sandbox,
 * PreLive and every dev/UAT deployment, and 404s it in Live — so a live gateway
 * has nothing to show however this app is configured. Anything that is not a
 * usable table means render nothing, which also covers a timeout, a proxy error
 * object, or HTML from a captive portal. A decorative panel must never be able to
 * break a payment page.
 */

export interface SandboxInstrument {
  suffix: string;
  label: string;
}

export interface SandboxInstrumentGroup {
  paymentType: string;
  instruments: SandboxInstrument[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function normaliseInstrumentGroups(payload: unknown): SandboxInstrumentGroup[] {
  if (!Array.isArray(payload)) return [];

  const groups: SandboxInstrumentGroup[] = [];
  for (const raw of payload) {
    if (!isRecord(raw) || !nonEmptyString(raw.paymentType)) continue;

    const instruments = Array.isArray(raw.instruments)
      ? raw.instruments.filter(
          (entry): entry is SandboxInstrument =>
            isRecord(entry) && nonEmptyString(entry.suffix) && nonEmptyString(entry.label),
        )
      : [];

    // A heading with nothing under it tells a tester less than no heading.
    if (instruments.length === 0) continue;
    groups.push({ paymentType: raw.paymentType, instruments });
  }
  return groups;
}
